import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpenIcon, Check, UploadIcon } from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import BreadcrumbNav from "@component/breadcrumb-nav";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { TrainingPersonaPicker } from "@component/training/training-persona-picker";
import { TrainingCsvUploader } from "@component/training/training-csv-uploader";
import { SendInvitesConfirmDialog } from "@component/training/send-invites-confirm-dialog";
import { EmailPreview } from "@component/training/email-preview";
import { RubricEditor } from "@container/training/rubric-editor";
import { createCampaign, getWorkspaceTeamLeaders, listPersonas, pickPersonaName, updateRubric } from "@service/training";
import type { WorkspaceTeamLeader } from "@service/training";
import {
    KB_SCOPES_ENABLED,
    excludeCampaignKnowledge,
    listAllKbDocuments,
    uploadKbDocuments,
    type KbDocument,
} from "@service/knowledge-base";
import { KbDocRow } from "@component/training/campaign-knowledge-panel";
import type { EmployeeRow, TrainingPersona, TrainingType, RubricCriterion, CampaignStatus } from "@type/training";
import {
    TRAINING_TYPE_LABEL,
    getDefaultRubric,
    SUPERBANK_LIVECHAT_RUBRIC,
    SUPERBANK_LIVECHAT_TEMPLATE,
} from "@type/training";
import { isSuperbankCompany } from "@constant/company";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

type Step = number;

// The optional Knowledge step slots between Rubric and Review; it only exists
// while the scoped-KB flag is on (its BE endpoints ship with KB Phase 1).
const STEP_LABELS = KB_SCOPES_ENABLED
    ? ["Details", "Type & persona", "Employees", "Rubric", "Knowledge", "Review"]
    : ["Details", "Type & persona", "Employees", "Rubric", "Review"];
const KNOWLEDGE_STEP = KB_SCOPES_ENABLED ? 5 : -1;
const REVIEW_STEP = STEP_LABELS.length;

function formatFileSize(bytes: number): string {
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
    return `${Math.round(bytes / 1_000)} KB`;
}

// Extract the backend's human-facing rejection from the shared
// `{ code, message, errors: string[] }` envelope so the toast shows WHY the
// request was rejected (e.g. "Campaign limit reached for your plan" +
// "Your workspace is limited to 2 campaigns."). Falls back to a generic line.
function campaignErrorParts(data: unknown): { title: string; description?: string } {
    const fallback = "Couldn't create the campaign. Please try again.";
    if (!data || typeof data !== "object") return { title: fallback };
    const d = data as { message?: unknown; errors?: unknown };
    const title =
        typeof d.message === "string" && d.message.trim() ? d.message.trim() : fallback;
    const description =
        Array.isArray(d.errors) && typeof d.errors[0] === "string" && d.errors[0].trim()
            ? d.errors[0].trim()
            : undefined;
    return { title, description };
}

export default function TrainingCampaignCreatePage() {
    // TrainingAuthLayout owns the SnackbarProvider, so the page body (which calls
    // useSnackbar) must render *inside* it as a child — calling useSnackbar at this
    // top level throws "useSnackbar must be used within a SnackbarProvider".
    return (
        <TrainingAuthLayout>
            <TrainingCampaignCreateContent />
        </TrainingAuthLayout>
    );
}

function TrainingCampaignCreateContent() {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [, userInfo] = useUserProfileContext();
    const companyName = userInfo?.company_name ?? "";
    // Superbank's Customer Service default is their own Bahasa-Indonesia live-chat
    // rubric, not the generic CS template. Gated by company_id (@constant/company).
    const isSuperbank = isSuperbankCompany(userInfo?.company_id);
    // Seed rubric for a chosen training type, substituting Superbank's rubric for
    // their Customer Service default.
    const seedRubricForType = (type: TrainingType): RubricCriterion[] =>
        isSuperbank && type === "customer_service"
            ? SUPERBANK_LIVECHAT_RUBRIC
            : getDefaultRubric(type);
    // Trial/usage gate — blocks publishing/saving a campaign when restricted.
    const { isTrainingCampaignBlocked } = useSubscription();
    const campaignBlockedTitle = isTrainingCampaignBlocked
        ? "Your trial has expired or your campaign limit is reached. Upgrade to create campaigns."
        : undefined;

    const [step, setStep] = useState<Step>(1);
    const [name, setName] = useState("");
    const [teamLeader, setTeamLeader] = useState("");
    const [teamLeaderOptions, setTeamLeaderOptions] = useState<WorkspaceTeamLeader[]>([]);
    const [employees, setEmployees] = useState<EmployeeRow[]>([]);
    const [trainingType, setTrainingType] = useState<TrainingType | null>("customer_service");
    const [personaId, setPersonaId] = useState<string | null>(null);
    const [personas, setPersonas] = useState<TrainingPersona[]>([]);
    const [personasLoading, setPersonasLoading] = useState(false);
    // Seeded from the Customer Service template; re-seeded from the selected
    // training type's template when the user changes the type in step 2.
    const [rubricCriteria, setRubricCriteria] = useState<RubricCriterion[]>(() => getDefaultRubric("customer_service"));
    // The profile (company_id) hydrates after the initial CS seed above. Once we
    // know the user is Superbank and the type is still Customer Service, swap in
    // their rubric — one-shot so it never clobbers later edits or a type change.
    const superbankSeededRef = useRef(false);
    useEffect(() => {
        if (superbankSeededRef.current) return;
        if (isSuperbank && trainingType === "customer_service") {
            setRubricCriteria(SUPERBANK_LIVECHAT_RUBRIC);
            superbankSeededRef.current = true;
        }
    }, [isSuperbank, trainingType]);
    const [submitting, setSubmitting] = useState(false);
    // Which action is in flight, so the two review buttons can show distinct
    // labels ("Saving..." vs "Publishing...") off a single submitting guard.
    const [submitMode, setSubmitMode] = useState<"draft" | "active" | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Optional overrides for the invitation email. When `null` (untouched),
    // EmailPreview falls back to its computed defaults; once the user edits
    // either field we hold their value verbatim — even if name/persona change
    // later — so their copy isn't silently clobbered.
    const [emailSubject, setEmailSubject] = useState<string | null>(null);
    const [emailBody, setEmailBody] = useState<string | null>(null);
    // Knowledge step (flag-gated). Workspace docs apply to every campaign BY
    // DEFAULT — they render as already-included rows the user can remove
    // (an exclusion), never as something to opt into. New files queue locally;
    // uploads/exclusions are applied only AFTER the campaign is created (it
    // has no id until submit).
    const [knowledgeFiles, setKnowledgeFiles] = useState<File[]>([]);
    const [workspaceDocs, setWorkspaceDocs] = useState<KbDocument[]>([]);
    const [excludedWorkspaceIds, setExcludedWorkspaceIds] = useState<Set<string>>(new Set());
    const [knowledgeDragging, setKnowledgeDragging] = useState(false);
    const knowledgeFileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!KB_SCOPES_ENABLED) return;
        let cancelled = false;
        // Scope to workspace: these are the docs that apply to every campaign by
        // default. Once the Phase 1 backend returns campaign attachments too, an
        // unscoped list would leak other campaigns' docs in as workspace defaults.
        listAllKbDocuments({ scope: "workspace" })
            .then((docs) => {
                if (!cancelled) setWorkspaceDocs(docs);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    function toggleWorkspaceDocExclusion(id: string) {
        setExcludedWorkspaceIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    // Fetch team leader options; auto-default to current user.
    // The fallback name is always set from userInfo so campaign creation is
    // never blocked by a failed or slow permissions fetch.
    useEffect(() => {
        const fallbackName =
            [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") ||
            userInfo?.username ||
            "";
        getWorkspaceTeamLeaders()
            .then((options) => {
                setTeamLeaderOptions(options);
                if (!teamLeader && fallbackName) {
                    const match = options.find(
                        (o) => o.email === userInfo?.email || o.name === fallbackName,
                    );
                    setTeamLeader(match?.name ?? fallbackName);
                }
            })
            .catch(() => {
                if (!teamLeader && fallbackName) setTeamLeader(fallbackName);
            });
    }, [teamLeader, userInfo]);

    useEffect(() => {
        if (trainingType) {
            setPersonasLoading(true);
            listPersonas(trainingType).then((result) => {
                setPersonas(result);
                setPersonasLoading(false);
            });
        } else {
            setPersonas([]);
        }
        setPersonaId(null);
    }, [trainingType]);

    const canNext = () => {
        if (step === 1) return name.trim().length > 0;
        if (step === 2) return trainingType !== null && personaId !== null;
        if (step === 3) return employees.length > 0;
        if (step === 4) return rubricCriteria.reduce((sum, c) => sum + c.weight, 0) === 100;
        // Knowledge step is optional — always passable.
        return true;
    };

    const handleNext = () => {
        if (!canNext()) return;
        setStep((s) => Math.min(REVIEW_STEP, s + 1) as Step);
    };

    const handleBack = () => {
        setStep((s) => Math.max(1, s - 1) as Step);
    };

    function addKnowledgeFiles(fileList: FileList) {
        // PDF only (product decision 2026-07-16).
        const valid = Array.from(fileList).filter(
            (f) => `.${f.name.split(".").pop()?.toLowerCase()}` === ".pdf",
        );
        setKnowledgeFiles((prev) => [...prev, ...valid]);
    }

    // status "draft" → save without launching (no invitations sent yet);
    // status "active" → publish immediately and send invitations.
    const handleSubmit = async (status: CampaignStatus) => {
        if (!trainingType || !personaId) return;
        setConfirmOpen(false);
        setSubmitMode(status === "draft" ? "draft" : "active");
        setSubmitting(true);
        try {
            const campaign = await createCampaign({
                name: name.trim(),
                training_type: trainingType,
                persona_id: personaId,
                employees,
                team_leader: teamLeader.trim() || null,
                status,
                // Only send email overrides when the user actually customized
                // them. `null` here means untouched → backend uses the default
                // template.
                ...(emailSubject !== null ? { email_subject: emailSubject } : {}),
                ...(emailBody !== null ? { email_body: emailBody } : {}),
            });
            await updateRubric(
                campaign.id,
                rubricCriteria.map((c) => ({ ...c, weight: c.weight }))
            );
            // Knowledge applies AFTER creation (the campaign id doesn't exist
            // earlier). A failure here must not read as "campaign failed" — the
            // campaign is live — so warn and land on its page to retry there.
            if (
                KB_SCOPES_ENABLED &&
                (knowledgeFiles.length > 0 || excludedWorkspaceIds.size > 0)
            ) {
                try {
                    if (knowledgeFiles.length > 0) {
                        await uploadKbDocuments(knowledgeFiles, undefined, {
                            type: "campaign",
                            id: campaign.id,
                        });
                    }
                    for (const docId of Array.from(excludedWorkspaceIds)) {
                        await excludeCampaignKnowledge(campaign.id, docId);
                    }
                } catch {
                    snackbar.showError(
                        "Campaign created, but applying its knowledge documents failed.",
                        "Review them from the campaign's Knowledge tab.",
                    );
                    navigate(`/training/campaigns/${campaign.id}`);
                    return;
                }
            }
            navigate("/training/campaigns");
        } catch (err) {
            // Surface the backend's rejection reason instead of failing silently
            // (the request used to throw with no user-facing feedback).
            const data = (err as { response?: { data?: unknown } })?.response?.data;
            const { title, description } = campaignErrorParts(data);
            snackbar.showError(title, description);
            catchErrorLog(`[training] create campaign failed: ${title}`);
        } finally {
            setSubmitting(false);
            setSubmitMode(null);
        }
    };

    const selectedPersonaName =
        personas.find((p) => p.id === personaId)?.name ??
        (personaId ? pickPersonaName(personaId) : "—");

    return (
        <>
            <div>
                <BreadcrumbNav
                    items={[
                        { label: "Campaigns", href: "/training/campaigns" },
                        { label: "Create Campaign" },
                    ]}
                />

                <h1 className="text-2xl font-semibold text-gray-900 mt-4 mb-8">
                    Create Training Campaign
                </h1>

                <div className="flex gap-10 items-start">
                    {/* Vertical step indicator */}
                    <div className="w-48 flex-shrink-0 pt-2">
                        <VerticalStepper steps={STEP_LABELS} currentStep={step} />
                    </div>

                    {/* Step content */}
                    <div className="flex-1 min-w-0">
                        <Card>
                            <CardContent className="p-8">
                                {/* Step 1 — Campaign details */}
                                {step === 1 && (
                                    <div>
                                        <StepHeader
                                            title="Configure your campaign"
                                            description="Set the campaign name and assign a team leader. These details will be visible across the workspace."
                                        />
                                        <div className="space-y-5">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Campaign name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value.slice(0, 60))}
                                                        placeholder="e.g. Q2 CS Refresh, Customer Service Team"
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 pr-14 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" && canNext())
                                                                handleNext();
                                                        }}
                                                        autoFocus
                                                    />
                                                    <span className={cn(
                                                        "absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none",
                                                        name.length >= 55 ? "text-orange-400" : "text-gray-300"
                                                    )}>
                                                        {name.length}/60
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Team leader <span className="text-red-500">*</span>
                                                </label>
                                                {teamLeaderOptions.length > 0 ? (
                                                    <Select
                                                        value={teamLeader || undefined}
                                                        onValueChange={setTeamLeader}
                                                    >
                                                        <SelectTrigger className="w-72">
                                                            <SelectValue placeholder="Select a team leader..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {teamLeaderOptions.map((tl) => (
                                                                <SelectItem key={tl.user_id} value={tl.name}>{tl.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={teamLeader}
                                                        onChange={(e) => setTeamLeader(e.target.value)}
                                                        placeholder="Team leader name"
                                                        className="w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                )}
                                                <p className="text-xs text-gray-400 mt-1.5">
                                                    Defaults to the campaign creator. Can be changed later.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2 — Type & Persona */}
                                {step === 2 && (
                                    <div>
                                        <StepHeader
                                            title="Choose a training type and persona"
                                            description="Select the type of training, then pick the AI persona your employees will interact with during their roleplay session."
                                        />
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Training type
                                            </label>
                                            <Select
                                                value={trainingType ?? ""}
                                                onValueChange={(v) => {
                                                    const next = v as TrainingType;
                                                    setTrainingType(next);
                                                    // Pre-select the rubric template for the chosen
                                                    // type. This replaces any earlier rubric edits,
                                                    // which is fine on a forward pass: the type (step
                                                    // 2) is chosen before the rubric (step 4).
                                                    setRubricCriteria(seedRubricForType(next));
                                                }}
                                            >
                                                <SelectTrigger className="w-72">
                                                    <SelectValue placeholder="Select a training type..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(
                                                        Object.entries(
                                                            TRAINING_TYPE_LABEL
                                                        ) as [TrainingType, string][]
                                                    ).map(([value, label]) => (
                                                        <SelectItem key={value} value={value}>
                                                            {label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {!trainingType && (
                                            <p className="text-sm text-gray-400 italic">
                                                Select a training type above to see available personas.
                                            </p>
                                        )}

                                        {trainingType && personasLoading && (
                                            <p className="text-sm text-gray-400">Loading personas...</p>
                                        )}

                                        {trainingType && !personasLoading && personas.length === 0 && (
                                            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
                                                <p className="text-sm font-medium text-gray-700 mb-1">
                                                    No personas available yet.
                                                </p>
                                                <p className="text-sm text-gray-400">
                                                    We're still building personas for{" "}
                                                    {TRAINING_TYPE_LABEL[trainingType]} training.
                                                    In the meantime, try{" "}
                                                    <button
                                                        type="button"
                                                        className="text-blue-600 hover:underline"
                                                        onClick={() => {
                                                            setTrainingType("customer_service");
                                                            setRubricCriteria(seedRubricForType("customer_service"));
                                                        }}
                                                    >
                                                        Customer Service
                                                    </button>
                                                    {" "}to get started.
                                                </p>
                                            </div>
                                        )}

                                        {trainingType && !personasLoading && personas.length > 0 && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                                    Persona
                                                </label>
                                                <TrainingPersonaPicker
                                                    personas={personas}
                                                    selectedId={personaId}
                                                    onSelect={setPersonaId}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Step 3 — Employees */}
                                {step === 3 && (
                                    <div>
                                        <StepHeader
                                            title="Add employees"
                                            description="Add people to this campaign by typing them in directly, or import a list via CSV."
                                        />
                                        <TrainingCsvUploader
                                            onParsed={setEmployees}
                                            showManual
                                            initialRows={employees}
                                        />
                                    </div>
                                )}

                                {/* Step 4 — Rubric */}
                                {step === 4 && (
                                    <div>
                                        <StepHeader
                                            title="Set scoring weights"
                                            description="Assign a percentage weight to each criterion. Weights must add up to 100%. You can adjust these later from the campaign page."
                                        />
                                        <RubricEditor
                                            value={rubricCriteria}
                                            onChange={setRubricCriteria}
                                            showOnlyWeights
                                            trainingType={trainingType ?? "customer_service"}
                                            optionsTemplate={
                                                isSuperbank &&
                                                (trainingType ?? "customer_service") === "customer_service"
                                                    ? SUPERBANK_LIVECHAT_TEMPLATE
                                                    : undefined
                                            }
                                        />
                                    </div>
                                )}

                                {/* Step 5 (flag-gated) — Knowledge */}
                                {KB_SCOPES_ENABLED && step === KNOWLEDGE_STEP && (
                                    <div>
                                        <StepHeader
                                            title="Add knowledge documents"
                                            description="Documents attached here will only be used when evaluating this campaign's roleplays. This step is optional."
                                        />

                                        {/* Section 1 — inline upload dropzone (no extra click to reveal) */}
                                        <div className="mb-6">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                                                Upload documents
                                            </div>
                                            <div
                                                onDragOver={(e) => { e.preventDefault(); setKnowledgeDragging(true); }}
                                                onDragLeave={(e) => { e.preventDefault(); setKnowledgeDragging(false); }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setKnowledgeDragging(false);
                                                    if (e.dataTransfer.files.length > 0) addKnowledgeFiles(e.dataTransfer.files);
                                                }}
                                                onClick={() => knowledgeFileInputRef.current?.click()}
                                                className={cn(
                                                    "flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
                                                    knowledgeDragging
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-300 hover:border-blue-400 hover:bg-gray-50",
                                                )}
                                            >
                                                <UploadIcon className="w-8 h-8 mb-3 text-gray-300" />
                                                <p className="text-sm font-semibold text-gray-700 mb-0.5">
                                                    Drag &amp; drop files here
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    or click to browse · PDF
                                                </p>
                                                <input
                                                    ref={knowledgeFileInputRef}
                                                    type="file"
                                                    multiple
                                                    accept=".pdf"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        if (e.target.files?.length) addKnowledgeFiles(e.target.files);
                                                        e.target.value = "";
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Section 2 — everything this campaign will be evaluated
                                            with: new uploads + workspace docs (included by default,
                                            removable per-campaign). */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                    Documents included in this campaign
                                                </div>
                                                <span className="text-xs text-gray-500 tabular-nums">
                                                    {knowledgeFiles.length +
                                                        workspaceDocs.filter((d) => !excludedWorkspaceIds.has(d.id)).length}{" "}
                                                    included
                                                </span>
                                            </div>
                                            {knowledgeFiles.length === 0 && workspaceDocs.length === 0 ? (
                                                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
                                                    <p className="text-sm text-gray-500">
                                                        No documents yet — upload above, or add
                                                        workspace-wide documents from the Knowledge Base tab.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                                                    {/* Workspace docs pinned first — the default set;
                                                        new uploads append below them. */}
                                                    {workspaceDocs.map((doc) => {
                                                        const isExcluded = excludedWorkspaceIds.has(doc.id);
                                                        return (
                                                            <div
                                                                key={doc.id}
                                                                className={cn(isExcluded && "opacity-50 bg-gray-50")}
                                                            >
                                                                <div className="flex items-center gap-3 px-4 py-3">
                                                                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                                        <BookOpenIcon className="w-4 h-4 text-blue-600" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className={cn("text-sm font-medium truncate", isExcluded ? "text-gray-500 line-through" : "text-gray-900")}>
                                                                            {doc.name}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                                            {doc.fileType} · {formatFileSize(doc.sizeBytes)} · Workspace-wide
                                                                            {isExcluded && " · Removed from this campaign"}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => toggleWorkspaceDocExclusion(doc.id)}
                                                                        className={cn(
                                                                            "text-xs font-medium flex-shrink-0 px-2 py-1 rounded-md transition-colors",
                                                                            isExcluded
                                                                                ? "text-blue-600 hover:bg-blue-50"
                                                                                : "text-gray-400 hover:text-rose-600 hover:bg-rose-50",
                                                                        )}
                                                                        title={
                                                                            isExcluded
                                                                                ? "Include this workspace document again"
                                                                                : "Remove this workspace document from this campaign only"
                                                                        }
                                                                    >
                                                                        {isExcluded ? "Include again" : "Remove"}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {knowledgeFiles.map((file, i) => (
                                                        <KbDocRow
                                                            key={`${file.name}:${file.size}:${i}`}
                                                            name={file.name}
                                                            subtitle={`${formatFileSize(file.size)} · New upload · This campaign only`}
                                                            onRemove={() =>
                                                                setKnowledgeFiles((prev) =>
                                                                    prev.filter((_, j) => j !== i),
                                                                )
                                                            }
                                                            removeTitle="Remove"
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            <p className="text-[11px] text-gray-400 mt-2">
                                                Workspace-wide documents apply to every campaign by default.
                                                Removing one here only affects this campaign.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Final step — Review */}
                                {step === REVIEW_STEP && (
                                    <div>
                                        <StepHeader
                                            title="Review and send invites"
                                            description="Check your campaign details before sending. Each employee will receive a unique invite link, valid for 7 days."
                                        />

                                        {/* Campaign summary */}
                                        <div className="mb-6">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                                                Campaign summary
                                            </div>
                                            <div className="space-y-3">
                                                <ReviewRow
                                                    label="Campaign name"
                                                    value={name}
                                                />
                                                <ReviewRow
                                                    label="Team leader"
                                                    value={teamLeader || "—"}
                                                />
                                                <ReviewRow
                                                    label="Training type"
                                                    value={
                                                        trainingType
                                                            ? TRAINING_TYPE_LABEL[trainingType]
                                                            : "—"
                                                    }
                                                />
                                                <ReviewRow
                                                    label="Persona"
                                                    value={selectedPersonaName}
                                                />
                                            </div>
                                        </div>

                                        {/* Knowledge documents — full names, since Review is the
                                            last chance to check the configuration. */}
                                        {KB_SCOPES_ENABLED && (
                                            <div className="mb-6">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                        Knowledge documents
                                                    </div>
                                                    <span className="text-xs text-gray-500 tabular-nums">
                                                        {knowledgeFiles.length +
                                                            workspaceDocs.filter((d) => !excludedWorkspaceIds.has(d.id)).length}{" "}
                                                        included
                                                    </span>
                                                </div>
                                                {knowledgeFiles.length === 0 &&
                                                workspaceDocs.filter((d) => !excludedWorkspaceIds.has(d.id)).length === 0 ? (
                                                    <p className="text-xs text-gray-500 italic px-1">
                                                        No knowledge documents — this campaign will be evaluated
                                                        on the rubric alone.
                                                    </p>
                                                ) : (
                                                    <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                                                        {workspaceDocs
                                                            .filter((d) => !excludedWorkspaceIds.has(d.id))
                                                            .map((doc) => (
                                                                <div key={doc.id} className="flex items-center gap-2.5 px-4 py-2.5">
                                                                    <BookOpenIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                                    <span className="text-sm text-gray-800 truncate flex-1">{doc.name}</span>
                                                                    <span className="text-xs text-gray-400 flex-shrink-0">Workspace-wide</span>
                                                                </div>
                                                            ))}
                                                        {knowledgeFiles.map((file, i) => (
                                                            <div key={`${file.name}:${i}`} className="flex items-center gap-2.5 px-4 py-2.5">
                                                                <UploadIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                                <span className="text-sm text-gray-800 truncate flex-1">{file.name}</span>
                                                                <span className="text-xs text-gray-400 flex-shrink-0">This campaign only</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {excludedWorkspaceIds.size > 0 && (
                                                    <p className="text-[11px] text-gray-400 mt-2">
                                                        {excludedWorkspaceIds.size} workspace document
                                                        {excludedWorkspaceIds.size === 1 ? "" : "s"} removed from
                                                        this campaign.
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Employees table */}
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                    Employees
                                                </div>
                                                <span className="text-xs text-gray-500 tabular-nums">
                                                    {employees.length} {employees.length === 1 ? "person" : "people"}
                                                </span>
                                            </div>
                                            {employees.length === 0 ? (
                                                <p className="text-xs text-gray-500 italic px-1">
                                                    No employees added yet.
                                                </p>
                                            ) : (
                                                <div className="rounded-lg border border-gray-200 overflow-hidden">
                                                    <div className="max-h-72 overflow-y-auto">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50 sticky top-0">
                                                                <tr className="border-b border-gray-200">
                                                                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                                                                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                                                                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Role</th>
                                                                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Department</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-100">
                                                                {employees.map((e, i) => (
                                                                    <tr key={`${e.email}-${i}`}>
                                                                        <td className="px-4 py-2.5 text-gray-900 font-medium whitespace-nowrap">{e.name || <span className="text-gray-400">—</span>}</td>
                                                                        <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{e.email || <span className="text-gray-400">—</span>}</td>
                                                                        <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{e.role || <span className="text-gray-400">—</span>}</td>
                                                                        <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{e.department || <span className="text-gray-400">—</span>}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Scoring rubric — read-only, weights only */}
                                        <div className="mb-6">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                                                Scoring rubric
                                            </div>
                                            <RubricEditor
                                                value={rubricCriteria}
                                                onChange={() => { /* read-only in review */ }}
                                                readOnly
                                                showOnlyWeights
                                                trainingType={trainingType ?? "customer_service"}
                                                optionsTemplate={
                                                    isSuperbank &&
                                                    (trainingType ?? "customer_service") === "customer_service"
                                                        ? SUPERBANK_LIVECHAT_TEMPLATE
                                                        : undefined
                                                }
                                            />
                                        </div>

                                        {/* Invitation email — editable. Defaults come
                                            from EmailPreview's template; user edits
                                            override and persist across step navigation. */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                    Invitation email
                                                </div>
                                                {(emailSubject !== null || emailBody !== null) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEmailSubject(null);
                                                            setEmailBody(null);
                                                        }}
                                                        className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                                    >
                                                        Reset to default
                                                    </button>
                                                )}
                                            </div>
                                            <EmailPreview
                                                simplified
                                                editable
                                                employerName={companyName || "Your Company"}
                                                campaignName={name || "Q2 Training"}
                                                trainingType={trainingType}
                                                personaName={selectedPersonaName}
                                                subjectValue={emailSubject ?? undefined}
                                                bodyValue={emailBody ?? undefined}
                                                onSubjectChange={setEmailSubject}
                                                onBodyChange={setEmailBody}
                                            />
                                            <p className="text-[11px] text-gray-400 mt-2">
                                                You can include <code className="bg-blue-50 text-blue-600 rounded px-1 py-0.5 font-mono">{`{{ firstName }}`}</code> to personalise each recipient.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Navigation */}
                                <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                                    {step > 1 ? (
                                        <Button
                                            variant="ghost"
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                    ) : (
                                        <div />
                                    )}
                                    {step < REVIEW_STEP ? (
                                        <Button
                                            onClick={handleNext}
                                            disabled={!canNext() || submitting}
                                        >
                                            Continue
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="outline"
                                                onClick={() => handleSubmit("draft")}
                                                disabled={submitting || isTrainingCampaignBlocked}
                                                title={campaignBlockedTitle}
                                            >
                                                {submitMode === "draft" ? "Saving..." : "Save as Draft"}
                                            </Button>
                                            <Button
                                                onClick={() => setConfirmOpen(true)}
                                                disabled={submitting || isTrainingCampaignBlocked}
                                                title={campaignBlockedTitle}
                                            >
                                                {submitMode === "active"
                                                    ? "Publishing..."
                                                    : `Publish & Send ${employees.length > 0 ? employees.length + " " : ""}Invite${employees.length !== 1 ? "s" : ""}`}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            <SendInvitesConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => handleSubmit("active")}
                employeeCount={employees.length}
                personaName={selectedPersonaName}
                submitting={submitting}
            />

        </>
    );
}

function VerticalStepper({
    steps,
    currentStep,
}: {
    steps: string[];
    currentStep: number;
}) {
    return (
        <nav className="flex flex-col">
            {steps.map((label, idx) => {
                const stepNum = idx + 1;
                const done = stepNum < currentStep;
                const active = stepNum === currentStep;
                const isLast = idx === steps.length - 1;
                return (
                    <div key={label} className="flex gap-3 items-start">
                        <div className="flex flex-col items-center">
                            <div
                                className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 transition-colors",
                                    done
                                        ? "bg-blue-600 text-white"
                                        : active
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-100 text-gray-400 border border-gray-200"
                                )}
                            >
                                {done ? <Check className="h-4 w-4" /> : stepNum}
                            </div>
                            {!isLast && (
                                <div
                                    className={cn(
                                        "w-px my-1 flex-1 min-h-[2.5rem]",
                                        done ? "bg-blue-300" : "bg-gray-200"
                                    )}
                                />
                            )}
                        </div>
                        <div className="flex h-8 items-center">
                            <span
                                className={cn(
                                    "text-sm font-medium leading-none",
                                    active
                                        ? "text-gray-900"
                                        : done
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                )}
                            >
                                {label}
                            </span>
                        </div>
                    </div>
                );
            })}
        </nav>
    );
}

function StepHeader({
    title,
    description,
}: {
    title: string;
    description: string;
}) {
    return (
        <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">{title}</h2>
            <p className="text-sm text-gray-500">{description}</p>
        </div>
    );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between py-2 border-b border-gray-100">
            <div className="text-sm text-gray-500">{label}</div>
            <div className="text-sm font-medium text-gray-900">{value}</div>
        </div>
    );
}
